import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  Button,
  Input,
  VStack,
  Text,
  Heading,
  Image,
} from "@chakra-ui/react"
import Turnstile from "react-turnstile"

import { useCore } from "@app/hooks/useCore"
import { useAnalytics } from "@app/hooks/useAnalytics"
import { useConfigContext } from "@app/providers/config"
import { useBackInStock } from "@app/hooks/useBackInStock"
import { useBraze } from "@app/hooks/useBraze"
import { useOutOfStock } from "@app/hooks/useOutOfStock"
import { useTurnstile } from "@app/hooks/useTurnstile"
import { useFunctions } from "@app/hooks/useFunctions"

import sparkles from "@static/images/sparkles.png"

type Props = {
  isOpen: boolean
  onClose: () => void
  product: any
  variant?: any
  /** GA4 `location` param for notify_me_click / notify_me_submit: quick_add / pdp (Productive #4293) */
  location?: string
}

const ProductBackInStock: React.FC<Props> = ({ isOpen, onClose, product, variant, location = "pdp" }) => {
  const { callFunction } = useFunctions()
  const {
    helpers: { decodeShopifyId },
  } = useCore()
  const { store, settings } = useConfigContext()
  const initialRef = useRef<HTMLInputElement>(null)
  const {
    title1,
    title2,
    content1,
    subContent1,
    content2,
    button1,
    emailPlaceholder,
    errorMessage,
    comingSoonTitle1,
    comingSoonContent1,
    comingSoonSubContent1,
    comingSoonButton1,
    comingSoonTitle2,
    comingSoonContent2,
  } = useBackInStock(product)
  const { stockValue } = useOutOfStock(product)
  const isComingSoon = stockValue === settings.product.comingSoon
  const firstScreenTitle = isComingSoon ? comingSoonTitle1 || title1 : title1
  const firstScreenContent = isComingSoon && comingSoonContent1.length ? comingSoonContent1 : content1
  const firstScreenSubContent = isComingSoon && comingSoonSubContent1.length ? comingSoonSubContent1 : subContent1
  const firstScreenButton = isComingSoon ? comingSoonButton1 || button1 : button1
  const secondScreenTitle = isComingSoon ? comingSoonTitle2 || title2 : title2
  const secondScreenContent = isComingSoon && comingSoonContent2.length ? comingSoonContent2 : content2
  const { identify, trackOnlineSignup, trackCatalogSubscription, trackComingSoonSignup } = useBraze()
  const {
    turnstileError,
    isDisabled,
    turnstile_key,
    widgetId,
    turnstileToken,
    setIsDisabled,
    setTurnstileError,
    setTurnstileToken,
    setWidgetId,
  } = useTurnstile()
  const source = isComingSoon ? "coming_soon" : "back_in_stock"
  const { trackNotifyMeClick, trackNotifyMeSubmit } = useAnalytics()
  const [email, setEmail] = useState("")
  const [sent, setSent] = useState(false)
  const [loading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  // The modal only opens from a Notify Me click, so the open transition is the click event.
  useEffect(() => {
    if (isOpen) trackNotifyMeClick(product, variant || product?.variants?.[0], location)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const isValidEmail = (email: string) => {
    return email?.length > 3 && /\S+@\S+\.\S+/.test(email)
  }

  // Reset internal state whenever the modal is closed so it reopens fresh.
  const handleClose = useCallback(() => {
    onClose()
    setSent(false)
    setError(false)
    setEmail("")
  }, [onClose])

  useEffect(() => {
    if (turnstileError) {
      setIsDisabled(true)
      setIsLoading(false)
      if (widgetId) window?.turnstile?.reset(widgetId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnstileError])

  // Turnstile tokens are single-use; reset the widget after a failed submit so a
  // resubmit gets a fresh token instead of reusing a consumed one (which Cloudflare
  // rejects as timeout-or-duplicate).
  const resetTurnstile = () => {
    setTurnstileToken(null)
    setIsDisabled(true)
    if (widgetId) window?.turnstile?.reset(widgetId)
  }

  // Once submitted successfully, show the confirmation briefly then dismiss.
  useEffect(() => {
    if (!sent) return
    const timeout = setTimeout(() => {
      handleClose()
    }, 2500)
    return () => clearTimeout(timeout)
  }, [sent, handleClose])

  const onSubmit = async (e: any) => {
    e.preventDefault()

    if (isValidEmail(email)) {
      setIsLoading(true)
      setError(false)
      // Prefer the caller's selected variant (e.g. from the quick add drawer); fall back to the
      // first variant. Carried over from the AlertMe path this replaces: subscribing to
      // variants[0] regardless of selection sends the shopper a restock alert for the wrong size.
      const variantId = decodeShopifyId((variant || product?.variants?.[0])?.id, "ProductVariant")

      try {
        const result = await callFunction("customer-subscribe", { email, source, "cf-turnstile-response": turnstileToken })

        if (result.status === "success") {
          const numericId = result.customer?.id ? decodeShopifyId(result.customer.id, "Customer") : null
          if (numericId) await identify(numericId, { logLoginEvent: false })
          await trackOnlineSignup({ source })
          // Coming soon gets its own event carrying product context; catalog_subscription is
          // back-in-stock only, so a coming-soon product never subscribes to the Braze catalog.
          if (source === "coming_soon") {
            trackComingSoonSignup({
              productId: decodeShopifyId(product?.id, "Product"),
              productName: product?.title,
              productHandle: product?.handle,
              imageUrl: product?.images?.[0]?.originalSrc || product?.images?.[0]?.src,
            })
          } else {
            trackCatalogSubscription({
              id: variantId,
              type: ["back_in_stock"],
              catalogName: store.brazeCatalogName,
            })
          }
          setSent(true)
          trackNotifyMeSubmit(product, variant || product?.variants?.[0], location)
        } else {
          console.error("[ProductBackInStock] customer-subscribe failed", result)
          setError(true)
          resetTurnstile()
        }
      } catch (error) {
        console.error(error)
        setError(true)
        resetTurnstile()
      }

      setIsLoading(false)
    }
  }

  return (
    <Modal onClose={handleClose} isOpen={isOpen} initialFocusRef={initialRef} motionPreset="slideInBottom" isCentered size="4xl">
      <ModalOverlay />
      <ModalContent
        mx={6.25}
        maxWidth="442px"
        backgroundColor="grey.lightest"
        borderWidth="10px"
        borderStyle="solid"
        borderColor="primary.white"
        borderRadius={20}
      >
        <Flex direction="column" alignSelf="center" height="100%" paddingX={{ base: 5, md: 10 }} paddingY={{ base: 12, md: 10 }}>
          <ModalHeader padding="0">
            <Box display="flex" alignItems="center" justifyContent="center">
              {!sent ? (
                <>
                  <Heading as="h1" size="h4" marginBottom="8px">
                    {firstScreenTitle}
                  </Heading>
                </>
              ) : (
                <Flex alignItems="center" display="inline-flex" pt={12} pb={2}>
                  <Heading as="h1" size="h4">
                    {secondScreenTitle}
                  </Heading>
                  <Box marginLeft={1}>
                    <Image src={sparkles} height={10} />
                  </Box>
                </Flex>
              )}
            </Box>
          </ModalHeader>

          <ModalCloseButton marginTop="5px" backgroundColor="transparent" />

          <ModalBody textAlign="center" paddingTop="0" paddingX="0">
            {!sent ? (
              <>
                {firstScreenContent.map((text, idx) => (
                  <Text key={`content-${idx}`} size="lg" mb={6} marginTop={idx === 0 ? "0" : undefined}>
                    {text}
                  </Text>
                ))}
              </>
            ) : (
              <>
                {secondScreenContent.map((text, idx) => (
                  <Text key={`content-${idx}`} size="lg" mb={6}>
                    {text}
                  </Text>
                ))}
              </>
            )}
            <Flex as="form" direction="column" onSubmit={onSubmit}>
              <VStack spacing={3}>
                {!sent && (
                  <>
                    <Input
                      ref={initialRef}
                      marginTop="0 !important"
                      variant="primary"
                      name="email"
                      type="email"
                      placeholder={emailPlaceholder || "Email"}
                      aria-label={emailPlaceholder || "Email"}
                      onChange={e => setEmail(e.target.value)}
                      value={email}
                    />
                    {firstScreenSubContent.map((text, idx) => (
                      <Text key={`content-${idx}`} size="sm">
                        {text}
                      </Text>
                    ))}

                    {error && errorMessage && (
                      <Text alignSelf="flex-start" size="sm" color="text.error" mt={1}>
                        {errorMessage}
                      </Text>
                    )}
                  </>
                )}
              </VStack>

              {!sent && turnstile_key ? (
                <Turnstile
                  sitekey={turnstile_key}
                  onVerify={token => {
                    setTurnstileToken(token)
                    setIsDisabled(false)
                    setTurnstileError(null)
                  }}
                  onLoad={widgetId => {
                    setWidgetId(widgetId)
                    setIsDisabled(true)
                  }}
                  onExpire={() => {
                    setIsDisabled(true)
                    if (widgetId) window?.turnstile?.reset(widgetId)
                  }}
                  onError={error => {
                    setIsDisabled(true)
                    setTurnstileError(`Verification failed (${error}). Please refresh the page and try again.`)
                  }}
                />
              ) : null}

              {!sent && turnstileError && (
                <Text color="preset.red" size="sm">
                  {turnstileError}
                </Text>
              )}

              {!sent && (
                <Button
                  variant="primary"
                  px="75px"
                  marginTop={6}
                  width="100%"
                  type="submit"
                  isDisabled={loading || !isValidEmail(email) || isDisabled}
                  isLoading={loading}
                >
                  {firstScreenButton}
                </Button>
              )}
            </Flex>
          </ModalBody>
        </Flex>
      </ModalContent>
    </Modal>
  )
}

export default React.memo(ProductBackInStock)
